import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CheckCircle2, AlertTriangle, ExternalLink, Trash2, UploadCloud, FileCheck2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MONTHS_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const fmt = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) : "—";

/**
 * Sezione "Bilanci di periodo": l'utente carica il PDF cumulativo (es. 1/01-30/04).
 * L'app:
 *   - estrae Ricavi/Costi/Utile stampati sul PDF
 *   - somma i mesi corrispondenti dal DB e confronta
 *   - mostra ✅ verde se match, ⚠️ rosso se discrepanza
 *   - conserva il PDF: clic su "Apri PDF" → si apre in un nuovo tab.
 */
export const CumulativeBilanciSection = ({ clientId, year }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);

  const fetchItems = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/cumulative-bilanci`, {
        params: year ? { year } : {},
      });
      setItems(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/clients/${clientId}/cumulative-bilanci`,
        fd,
        { params: { year, period_start_month: 1, period_end_month: endMonth } }
      );
      const v = res.data?.validation || {};
      if (v.overall_match) {
        toast.success(`Bilancio cumulativo caricato — i numeri coincidono al centesimo ✅`);
      } else if (v.has_missing_months) {
        toast.warning(`PDF caricato. Attenzione: mancano i mesi ${(v.months_missing || []).map(m => MONTHS_IT[m-1]).join(", ")} nel database — la verifica è parziale.`);
      } else {
        const issues = [];
        if (v.revenue_match === false) issues.push(`Ricavi: ${fmt(v.sum_revenue_stored)} stored vs ${fmt(v.pdf_total_revenue)} PDF`);
        if (v.utile_match === false) issues.push(`Utile: ${fmt(v.sum_utile_stored)} stored vs ${fmt(v.pdf_utile)} PDF`);
        toast.error(`Discrepanza rilevata: ${issues.join(" — ")}`);
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleOpen = async (cumId) => {
    try {
      const res = await axios.get(
        `${API}/clients/${clientId}/cumulative-bilanci/${cumId}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Browser keeps the URL alive while the tab is open
    } catch (e) {
      toast.error("Errore apertura PDF");
    }
  };

  const handleDelete = async (cumId) => {
    if (!window.confirm("Sicuro di rimuovere questo bilancio cumulativo?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/cumulative-bilanci/${cumId}`);
      toast.success("Bilancio rimosso");
      fetchItems();
    } catch (e) {
      toast.error("Errore rimozione");
    }
  };

  const [aiSummaries, setAiSummaries] = useState({}); // { cumId: text }
  const [aiLoading, setAiLoading] = useState({});

  const handleAiSummary = async (cumId) => {
    setAiLoading((s) => ({ ...s, [cumId]: true }));
    try {
      const res = await axios.post(
        `${API}/clients/${clientId}/cumulative-bilanci/${cumId}/ai-summary`
      );
      setAiSummaries((s) => ({ ...s, [cumId]: res.data.summary }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione commento AI");
    } finally {
      setAiLoading((s) => ({ ...s, [cumId]: false }));
    }
  };

  return (
    <Card className="border-slate-200" data-testid="cumulative-bilanci-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-blue-700" />
          Bilanci di periodo (di confronto)
        </CardTitle>
        <CardDescription>
          Carica qui il bilancio cumulativo del periodo (es. 1/01–30/04). L'app verifica che la somma dei mesi salvati coincida coi totali stampati e ti permette di riaprire sempre il PDF originale.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Periodo cumulativo: 1° gennaio →</label>
            <Select value={String(endMonth)} onValueChange={(v) => setEndMonth(parseInt(v))}>
              <SelectTrigger className="w-44" data-testid="cumulative-end-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_FULL.map((m, i) => (
                  <SelectItem key={i+1} value={String(i+1)}>fine {m} {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label>
            <Button asChild disabled={uploading} data-testid="cumulative-upload-btn">
              <span className="cursor-pointer"><UploadCloud className="h-4 w-4 mr-2" /> {uploading ? "Carico..." : "Carica PDF cumulativo"}</span>
            </Button>
            <input type="file" accept=".pdf" onChange={handleUpload} className="hidden" />
          </label>
        </div>

        {loading && <div className="text-xs text-slate-500">Caricamento...</div>}

        {!loading && items.length === 0 && (
          <div className="text-xs text-slate-500 italic bg-slate-50 rounded p-3 border border-slate-200">
            Nessun bilancio cumulativo caricato per il {year}. Caricalo qui sopra: ti servirà come riferimento per controllare che i dati salvati coincidano col bilancio del commercialista.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-2" data-testid="cumulative-list">
            {items.map((it) => {
              const v = it.validation || {};
              const isOk = v.overall_match;
              const hasMissing = v.has_missing_months;
              const accent = isOk ? "border-emerald-200 bg-emerald-50/40"
                : hasMissing ? "border-slate-200 bg-slate-50"
                : "border-red-200 bg-red-50/40";
              const Icon = isOk ? CheckCircle2 : AlertTriangle;
              const iconColor = isOk ? "text-emerald-600" : hasMissing ? "text-slate-600" : "text-red-600";

              return (
                <div key={it.id} className={`rounded-lg border-2 p-3 ${accent}`} data-testid={`cumulative-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <Icon className={`h-4 w-4 mt-0.5 ${iconColor}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-800">
                          {MONTHS_FULL[it.period_start_month - 1]} – {MONTHS_FULL[it.period_end_month - 1]} {it.year}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {it.file_name} · {Math.round(it.file_size / 1024)} KB · caricato {new Date(it.uploaded_at).toLocaleDateString("it-IT")}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => handleAiSummary(it.id)} disabled={aiLoading[it.id]} data-testid={`cumulative-ai-${it.id}`}>
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> {aiLoading[it.id] ? "Genero..." : "Commento AI"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleOpen(it.id)} data-testid={`cumulative-open-${it.id}`}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Apri PDF
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(it.id)} className="text-red-600 hover:bg-red-50" data-testid={`cumulative-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* AI summary, if generated */}
                  {aiSummaries[it.id] && (
                    <div className="mt-2 rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-slate-700 leading-relaxed whitespace-pre-line" data-testid={`cumulative-ai-output-${it.id}`}>
                      <div className="flex items-center gap-1 text-[10px] font-bold text-violet-700 mb-1">
                        <Sparkles className="h-3 w-3" /> COMMENTO AI · da copiare nella mail al cliente
                      </div>
                      {aiSummaries[it.id]}
                    </div>
                  )}

                  {/* Comparison table */}
                  <div className="mt-2 grid sm:grid-cols-3 gap-2 text-[11px]">
                    <ComparisonRow label="Ricavi" stored={v.sum_revenue_stored} pdf={v.pdf_total_revenue} match={v.revenue_match} />
                    <ComparisonRow label="Utile" stored={v.sum_utile_stored} pdf={v.pdf_utile} match={v.utile_match} />
                    <div className="bg-white/60 rounded px-2 py-1.5 border">
                      <div className="text-slate-500">Mesi salvati</div>
                      <div className="font-semibold text-slate-700">
                        {(v.months_present || []).map(m => MONTHS_IT[m-1]).join(" · ") || "—"}
                      </div>
                      {hasMissing && (
                        <div className="text-red-600 text-[10px] mt-0.5">Mancano: {(v.months_missing || []).map(m => MONTHS_IT[m-1]).join(", ")}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ComparisonRow = ({ label, stored, pdf, match }) => (
  <div className={`bg-white/60 rounded px-2 py-1.5 border ${match === false ? "border-red-300" : "border-slate-200"}`}>
    <div className="text-slate-500">{label}</div>
    <div className="flex items-center justify-between gap-1">
      <span className="font-semibold text-slate-700 tabular-nums">{fmt(stored)}</span>
      <span className="text-slate-400">vs</span>
      <span className="font-semibold tabular-nums text-slate-800">{fmt(pdf)}</span>
    </div>
    {match === true && <div className="text-emerald-700 text-[10px]">✅ Match</div>}
    {match === false && <div className="text-red-700 text-[10px]">⚠️ Differenza {fmt(Math.abs((stored||0) - (pdf||0)))}</div>}
    {match == null && <div className="text-slate-400 text-[10px]">Non disponibile</div>}
  </div>
);

export default CumulativeBilanciSection;
